import { useState, useEffect, useMemo } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Download,
  Trash2,
  MoreHorizontal,
  RefreshCw,
  Utensils,
  Users,
  Euro,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Sun,
  Moon,
} from "lucide-react";
import api from "../lib/api";

// Dynamic package label function
const getPackageLabel = (pkg) => {
  if (!pkg) return "-";
  const match = pkg.match(/^(\d+)_meals?$/);
  if (match) {
    const num = parseInt(match[1]);
    return `${num} Past${num === 1 ? 'o' : 'i'}`;
  }
  return pkg;
};

const DAY_LABELS = {
  "23_agosto_cena": "23 Agosto",
  "24_agosto": "24 Agosto",
  "25_agosto": "25 Agosto",
  "26_agosto": "26 Agosto",
  "27_agosto": "27 Agosto",
  "28_agosto": "28 Agosto",
  "29_agosto_pranzo": "29 Agosto",
};

const MEAL_LABELS = {
  "pranzo": "Pranzo",
  "cena": "Cena",
};

const STATUS_CONFIG = {
  pending: { label: "In Attesa", icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  confirmed: { label: "Confermato", icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10" },
  cancelled: { label: "Annullato", icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
};

export default function SendMeAdminPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, revenue: 0, confirmed: 0 });

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await api.get("/sendme/bookings");
      setBookings(response.data);
      
      // Calculate stats
      const total = response.data.length;
      const totalPeople = response.data.reduce((sum, b) => sum + (b.quantity || 1), 0);
      const revenue = response.data.reduce((sum, b) => sum + (b.total_price || b.price || 0), 0);
      const confirmed = response.data.filter(b => b.status === "confirmed").length;
      setStats({ total, totalPeople, revenue, confirmed });
    } catch (error) {
      toast.error("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await api.put(`/sendme/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success("Stato aggiornato");
      fetchBookings();
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa prenotazione?")) return;
    try {
      await api.delete(`/sendme/bookings/${bookingId}`);
      toast.success("Prenotazione eliminata");
      fetchBookings();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/sendme/bookings/export", {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `prenotazioni_sendme_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export completato");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const handleResetAll = async () => {
    const confirmText = window.prompt(
      "ATTENZIONE: Stai per eliminare TUTTE le prenotazioni!\n\nQuesta azione è irreversibile.\n\nScrivi 'RESET' per confermare:"
    );
    
    if (confirmText !== "RESET") {
      if (confirmText !== null) {
        toast.error("Operazione annullata. Devi scrivere esattamente 'RESET'");
      }
      return;
    }
    
    try {
      const response = await api.delete("/sendme/bookings/reset-all");
      toast.success(response.data.message || "Tutte le prenotazioni sono state eliminate");
      fetchBookings();
    } catch (error) {
      toast.error("Errore durante il reset");
    }
  };

  const formatMealInfo = (booking) => {
    // Check if new format with meals array
    if (booking.meals && booking.meals.length > 0) {
      return (
        <div className="text-xs space-y-1">
          {booking.meals.map((meal, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <span className="font-medium text-yellow-500">{idx + 1}:</span> 
              {DAY_LABELS[meal.day] || meal.day} 
              <span className="text-muted-foreground">({MEAL_LABELS[meal.meal_time] || meal.meal_time})</span>
            </div>
          ))}
        </div>
      );
    }
    
    // Old format fallback
    const day1 = DAY_LABELS[booking.day] || booking.day || "-";
    const meal1 = MEAL_LABELS[booking.meal_time] || booking.meal_time || "";
    
    if (booking.day2) {
      const day2 = DAY_LABELS[booking.day2] || booking.day2 || "-";
      const meal2 = MEAL_LABELS[booking.meal_time2] || booking.meal_time2 || "";
      return (
        <div className="text-xs space-y-1">
          <div className="flex items-center gap-1">
            <span className="font-medium">1:</span> {day1} <span className="text-muted-foreground">({meal1})</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="font-medium">2:</span> {day2} <span className="text-muted-foreground">({meal2})</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="text-xs">
        {day1} <span className="text-muted-foreground">({meal1})</span>
      </div>
    );
  };

  // Calculate meals per day
  const mealsByDay = useMemo(() => {
    const days = {};
    
    bookings.forEach(booking => {
      const qty = booking.quantity || 1;
      
      // Check if new format with meals array
      if (booking.meals && booking.meals.length > 0) {
        booking.meals.forEach(meal => {
          const dayKey = meal.day;
          const mealKey = meal.meal_time;
          if (!days[dayKey]) days[dayKey] = { pranzo: 0, cena: 0 };
          if (mealKey && days[dayKey][mealKey] !== undefined) {
            days[dayKey][mealKey] += qty;
          }
        });
      } else {
        // Old format fallback
        if (booking.day) {
          const dayKey = booking.day;
          const mealKey = booking.meal_time;
          if (!days[dayKey]) days[dayKey] = { pranzo: 0, cena: 0 };
          if (mealKey && days[dayKey][mealKey] !== undefined) {
            days[dayKey][mealKey] += qty;
          }
        }
        
        // Second meal (for old format)
        if (booking.day2) {
          const dayKey = booking.day2;
          const mealKey = booking.meal_time2;
          if (!days[dayKey]) days[dayKey] = { pranzo: 0, cena: 0 };
          if (mealKey && days[dayKey][mealKey] !== undefined) {
            days[dayKey][mealKey] += qty;
          }
        }
      }
    });
    
    return days;
  }, [bookings]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-primary font-bold">SendMe - Prenotazioni</h1>
          <p className="text-muted-foreground">Gestione prenotazioni pasti evento</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchBookings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Esporta Excel
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleResetAll}
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Reset Tutto
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Prenotazioni</p>
                <p className="text-3xl font-bold text-yellow-500">{stats.total}</p>
              </div>
              <Utensils className="w-10 h-10 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Persone</p>
                <p className="text-3xl font-bold text-purple-500">{stats.totalPeople || 0}</p>
              </div>
              <Users className="w-10 h-10 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Incasso Potenziale</p>
                <p className="text-3xl font-bold text-green-500">€{stats.revenue}</p>
              </div>
              <Euro className="w-10 h-10 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Confermate</p>
                <p className="text-3xl font-bold text-blue-500">{stats.confirmed}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meals by Day Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            Riepilogo Pasti per Giorno
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {Object.keys(DAY_LABELS).map((dayKey) => {
              const dayData = mealsByDay[dayKey] || { pranzo: 0, cena: 0 };
              const dayLabel = DAY_LABELS[dayKey];
              const isFirst = dayKey === "23_agosto_cena";
              const isLast = dayKey === "29_agosto_pranzo";
              
              return (
                <div 
                  key={dayKey} 
                  className="bg-muted/50 rounded-lg p-3 text-center border border-border"
                >
                  <div className="font-semibold text-sm mb-2">{dayLabel}</div>
                  <div className="space-y-1">
                    {!isFirst && (
                      <div className="flex items-center justify-center gap-1 text-xs">
                        <Sun className="w-3 h-3 text-yellow-500" />
                        <span className="text-muted-foreground">Pranzo:</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {dayData.pranzo}
                        </Badge>
                      </div>
                    )}
                    {!isLast && (
                      <div className="flex items-center justify-center gap-1 text-xs">
                        <Moon className="w-3 h-3 text-blue-400" />
                        <span className="text-muted-foreground">Cena:</span>
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          {dayData.cena}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground">Tot: </span>
                    <span className="font-bold text-accent">
                      {(isFirst ? 0 : dayData.pranzo) + (isLast ? 0 : dayData.cena)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Utensils className="w-5 h-5" />
            Lista Prenotazioni
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
            </div>
          ) : bookings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Utensils className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna prenotazione</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Pacchetto</TableHead>
                    <TableHead className="text-center">Persone</TableHead>
                    <TableHead>Pasti</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => {
                    const statusConfig = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <TableRow key={booking.id}>
                        <TableCell>
                          <div className="font-medium">{booking.name} {booking.surname}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(booking.created_at).toLocaleDateString("it-IT")}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{booking.phone}</div>
                          <div className="text-xs text-muted-foreground">{booking.email}</div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {getPackageLabel(booking.package)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="font-bold">
                            {booking.quantity || 1}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatMealInfo(booking)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-accent">
                          €{booking.total_price || booking.price || 0}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={booking.status}
                            onValueChange={(value) => handleStatusChange(booking.id, value)}
                          >
                            <SelectTrigger className={`w-32 h-8 ${statusConfig.bg} ${statusConfig.color} border-0`}>
                              <div className="flex items-center gap-1">
                                <StatusIcon className="w-3 h-3" />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">In Attesa</SelectItem>
                              <SelectItem value="confirmed">Confermato</SelectItem>
                              <SelectItem value="cancelled">Annullato</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleDelete(booking.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Elimina
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
